/* ============================================================================
 * errors.jsx — Centralized error capture + bottom error bar
 * ========================================================================= */

import React from 'react'

export const ERROR_CODES = {
  "WYF-AUTH-001": "Invalid email or password",
  "WYF-AUTH-002": "CAPTCHA verification failed",
  "WYF-AUTH-003": "Too many failed attempts — please wait",
  "WYF-AUTH-004": "Session expired — please sign in again",
  "WYF-AUTH-005": "Password reset email failed to send",
  "WYF-AUTH-006": "Password is too weak (min 8 chars, 1 number)",
  "WYF-AUTH-007": "Account is disabled — contact an administrator",
  "WYF-CFG-001": "Supabase URL or anon key missing in config.js",
  "WYF-CFG-002": "Supabase JS client failed to load (check internet/CDN)",
  "WYF-NET-001": "Network unreachable — check your internet connection",
  "WYF-NET-002": "Supabase server returned an unexpected error",
  "WYF-NET-003": "Request timed out",
  "WYF-DB-100": "Permission denied (Row-Level Security blocked the request)",
  "WYF-DB-101": "Unique constraint violation (duplicate value)",
  "WYF-DB-102": "Foreign key violation (related record missing)",
  "WYF-DB-103": "Required field missing or invalid",
  "WYF-DB-104": "Record not found",
  "WYF-DB-105": "RPC function returned an error",
  "WYF-DB-106": "Read query failed",
  "WYF-DB-107": "Write query failed",
  "WYF-VAL-001": "Phone number is not a valid format",
  "WYF-VAL-002": "Email format is invalid",
  "WYF-VAL-003": "Required fields are empty",
  "WYF-VAL-004": "Value out of allowed range",
  "WYF-BIZ-001": "Customer over credit limit",
  "WYF-BIZ-002": "Cannot delete: record is referenced elsewhere",
  "WYF-BIZ-003": "Order is already cancelled",
  "WYF-AI-001": "Receipt image upload failed",
  "WYF-AI-002": "AI processing failed — please try again",
  "WYF-AI-003": "AI could not read the receipt — try a clearer photo",
  "WYF-AI-004": "Production batch could not be logged",
  "WYF-AI-005": "File must be JPEG, PNG, or WebP under 5MB",
  "WYF-AI-006": "AI service unavailable — check your connection",
  "WYF-AI-007": "Purchase could not be logged — check your input and try again",
};

const _errorListeners = new Set();
let _currentError = null;

export function reportError(err) {
  const enriched = {
    code: err.code || "WYF-DB-107",
    where: err.where || "Unknown",
    message: err.message || ERROR_CODES[err.code] || "An error occurred",
    detail: err.detail || "",
    at: new Date().toISOString(),
  };
  _currentError = enriched;
  console.error(`[${enriched.code}] ${enriched.where}: ${enriched.message}`, enriched.detail);
  _errorListeners.forEach(fn => fn(enriched));
  return enriched;
}

export function clearError() {
  _currentError = null;
  _errorListeners.forEach(fn => fn(null));
}

export function subscribeError(fn) {
  _errorListeners.add(fn);
  fn(_currentError);
  return () => _errorListeners.delete(fn);
}

export async function wrap(code, where, op) {
  try {
    return await op();
  } catch (e) {
    reportError({
      code,
      where,
      message: ERROR_CODES[code] || "Operation failed",
      detail: e && (e.message || e.error_description || JSON.stringify(e)),
    });
    throw e;
  }
}

export function ErrorBar() {
  const [err, setErr] = React.useState(null);
  React.useEffect(() => subscribeError(setErr), []);
  if (!err) return null;
  return (
    <div className="err-bar" role="alert">
      <div className="ico">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12" y2="16" />
        </svg>
      </div>
      <div className="body">
        <strong>{err.message}</strong>
        <code>{err.code}</code>
        <div className="where">at {err.where} · {new Date(err.at).toLocaleTimeString()}</div>
        {err.detail ? <div className="where mono" style={{marginTop:4}} title={err.detail}>{String(err.detail).slice(0, 120)}{String(err.detail).length > 120 ? "…" : ""}</div> : null}
      </div>
      <div className="actions">
        <button className="btn btn-ghost" onClick={() => {
          navigator.clipboard?.writeText(JSON.stringify(err, null, 2));
        }}>Copy</button>
        <button className="btn btn-ghost" onClick={clearError}>Dismiss</button>
      </div>
    </div>
  );
}